import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { api } from '../../lib/api.js';
import { WorkspacesListResponse } from '@raven/shared-types';
import { DataTable, type Column } from '../../components/DataTable.js';

interface Row {
  workspace_id: string;
  client_conf_key: string;
  name?: string | null;
  modules: string[];
  is_suspended: boolean;
  environment?: string | null;
  reseller_id?: string | null;
}

export function WorkspacesPage() {
  const { data, isPending, error } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.get('/api/workspaces', WorkspacesListResponse),
  });

  const columns: Column<Row>[] = [
    {
      header: 'Workspace',
      cell: (r) => (
        <Link
          to="/workspaces/$id"
          params={{ id: r.workspace_id }}
          className="font-medium text-brand-700 hover:underline"
        >
          {r.name ?? r.client_conf_key}
        </Link>
      ),
    },
    { header: 'client_id', cell: (r) => <code className="text-xs">{r.workspace_id}</code> },
    { header: 'Env', cell: (r) => r.environment ?? '—' },
    {
      header: 'Modules',
      cell: (r) => (
        <div className="flex gap-1 flex-wrap">
          {r.modules.map((m) => (
            <span key={m} className="badge bg-slate-100 text-slate-700">
              {m}
            </span>
          ))}
        </div>
      ),
    },
    {
      header: 'Status',
      cell: (r) =>
        r.is_suspended ? (
          <span className="badge-error">suspended</span>
        ) : (
          <span className="badge-success">active</span>
        ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Workspaces</h1>
        <p className="text-sm text-slate-600">All tenants you have access to.</p>
      </header>
      {error ? (
        <p className="text-red-600 text-sm">{(error as Error).message}</p>
      ) : (
        <DataTable
          loading={isPending}
          rows={(data?.workspaces ?? []) as Row[]}
          columns={columns}
          emptyMessage="No workspaces visible. Ask your administrator for access."
        />
      )}
    </div>
  );
}
